/**
 * Fetching and choosing a login context on web.
 *
 * Mirrors the mobile hook of the same name so the two clients cannot drift.
 * The flow sits AFTER a normal sign-in rather than inside it, leaving the
 * existing login path untouched:
 *
 *   1. Login completes as it always has -> tokens in localStorage
 *   2. fetchContexts()  -> what may this person sign in as?
 *   3. choose(uid)      -> same session, re-issued with the context baked in
 *
 * When the backend has the feature switched off it answers 501 and
 * fetchContexts resolves false, so login proceeds exactly as it does today.
 * That is what makes this safe to ship before the server flag is turned on.
 */

import { useCallback, useState } from 'react';
import axios from 'axios';

import baseURL from '@/http';
import { getValidToken } from '@/pages/public/Login/checkTokenValidity';

import type { ContextsResponse, LoginContext } from './types';

const API_BASE = `${baseURL}/auth`;

/**
 * What the server says this account may do. Mirrors the mobile hook exactly so
 * the two clients cannot drift.
 *
 *   'unavailable' - feature off (501), offline, or malformed. Log in exactly as
 *                   before. Never block a login on this question.
 *   'none'        - authenticated, but linked to no institute and holding no
 *                   platform grant. Show the no-access dialog; do NOT route in.
 *   'single'      - exactly one context. Auto-select it, so the session still
 *                   gets a scoped token.
 *   'choose'      - more than one. Show the picker.
 */
export type ContextOutcome = 'unavailable' | 'none' | 'single' | 'choose';

/**
 * The outcome AND the freshly fetched list, returned together. The list is
 * also put into state for the dialog to render, but state is not visible to
 * the code that awaits fetchContexts() - React flushes it later, so an
 * auto-select reading state (or a ref mirrored from it in an effect) saw the
 * PREVIOUS value: [] on first sign-in, which silently skipped select-context
 * and left single-context users on an unscoped legacy token.
 */
export interface FetchContextsResult {
  outcome: ContextOutcome;
  contexts: LoginContext[];
}

interface UseLoginContextsResult {
  contexts: LoginContext[];
  loading: boolean;
  error: string;
  fetchContexts: () => Promise<FetchContextsResult>;
  /** Resolves true once the scoped tokens are stored. */
  choose: (membershipUid: string) => Promise<boolean>;
  reset: () => void;
}

export const useLoginContexts = (): UseLoginContextsResult => {
  const [contexts, setContexts] = useState<LoginContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchContexts = useCallback(async (): Promise<FetchContextsResult> => {
    setLoading(true);
    setError('');
    try {
      const token = await getValidToken();
      const { data } = await axios.get<ContextsResponse>(`${API_BASE}/contexts/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = data?.contexts ?? [];
      setContexts(list);

      if (list.length === 0) return { outcome: 'none', contexts: list };
      if (list.length === 1) return { outcome: 'single', contexts: list };
      return { outcome: 'choose', contexts: list };
    } catch (err: any) {
      // 501 means multi-tenant context is switched off server-side. That is the
      // current production state, not a failure - stay quiet and let login run.
      if (err?.response?.status === 501) return { outcome: 'unavailable', contexts: [] };

      console.warn('[useLoginContexts] failed to load contexts', err);
      // Deliberately NOT fatal: a network blip must not lock a legitimate user
      // out of the app.
      return { outcome: 'unavailable', contexts: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const choose = useCallback(async (membershipUid: string): Promise<boolean> => {
    setLoading(true);
    setError('');
    try {
      const token = await getValidToken();
      const { data } = await axios.post(
        `${API_BASE}/select-context/`,
        { membership_uid: membershipUid },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!data?.access || !data?.refresh) {
        setError('Could not open that institute. Please try again.');
        return false;
      }

      // Replace the pre-context tokens with the scoped pair. Every request after
      // this carries the institute inside a signed claim, so the backend no
      // longer has to trust an instituteId sent by the client.
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);

      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('That institute is no longer available to you.');
      } else if (status === 401) {
        setError('Your session expired. Please sign in again.');
      } else {
        console.warn('[useLoginContexts] select-context failed', err);
        setError('Could not open that institute. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setContexts([]);
    setError('');
    setLoading(false);
  }, []);

  return { contexts, loading, error, fetchContexts, choose, reset };
};
